export const seoContentTranslations = {
  en: {
    // PDF to XRechnung (UBL)
    pdfToUbl: {
      title: "PDF to XRechnung (UBL) Converter - Complete Guide",
      description: "Convert PDF to XRechnung UBL format. AI-powered converter for German public sector compliance (EN 16931) and Peppol integration.",
      features: [
        "AI-powered data extraction from PDF invoices with 99% accuracy",
        "Full compliance with XRechnung 2.3 and EN 16931 standards",
        "Automatic validation of mandatory fields (BR-DE rules)",
        "Support for all invoice types (380, 381, 384, 389)",
        "Batch processing for multiple invoices",
        "Instant preview and editing before download",
        "Peppol-ready UBL 2.1 format output"
      ],
      benefits: [
        "Save 90% time compared to manual data entry",
        "Eliminate errors with automated validation",
        "Meet German public sector requirements instantly",
        "No software installation required - 100% web-based",
        "Secure processing - your data never leaves EU servers",
        "24/7 availability with instant conversion"
      ],
      howItWorks: [
        { step: 1, title: "Upload PDF Invoice", description: "Drag and drop or select your PDF invoice file (up to 10 files, 5MB each)" },
        { step: 2, title: "AI Extraction & Review", description: "Our AI extracts all invoice data automatically. Review and edit any field as needed" },
        { step: 3, title: "Download XRechnung UBL", description: "Get your validated XRechnung UBL XML file ready for submission" }
      ],
      faq: [
        { 
          question: "What is XRechnung UBL format?", 
          answer: "XRechnung UBL (Universal Business Language) is the standardized XML format required for e-invoicing to German public authorities. It's based on UBL 2.1 and ensures compliance with European standard EN 16931." 
        },
        { 
          question: "Which organizations require XRechnung UBL?", 
          answer: "All German federal authorities, most state authorities, and many municipalities require XRechnung format. The UBL variant is particularly common for Peppol network transmissions." 
        },
        { 
          question: "How accurate is the PDF extraction?", 
          answer: "Our AI achieves 99% accuracy for standard invoice layouts. All extracted data can be reviewed and edited before conversion, ensuring 100% accuracy in the final output." 
        },
        {
          question: "Is my invoice data secure?",
          answer: "Yes, all processing happens on EU-based servers with bank-level encryption. Your data is automatically deleted after 24 hours, and we never store or share your invoice information."
        }
      ]
    },

    // PDF to XRechnung (CII)
    pdfToCii: {
      title: "PDF to XRechnung (CII) Converter - Enterprise Solution",
      description: "Transform PDF to XRechnung CII format for B2B/B2G transactions. Supports Cross Industry Invoice standard and ZUGFeRD.",
      features: [
        "Advanced OCR and AI for complex invoice layouts",
        "CII 16B format with full UN/CEFACT compliance",
        "Built-in ZUGFeRD 2.2 compatibility",
        "Automatic tax calculation and validation",
        "Multi-language invoice support (DE, EN, FR, IT, ES)",
        "Line-item level data extraction and mapping",
        "Export to accounting systems (DATEV, SAP)"
      ],
      benefits: [
        "Perfect for enterprise B2B invoicing workflows",
        "Compatible with all major ERP systems",
        "Reduce processing costs by up to 80%",
        "Ensure tax compliance with automatic validation",
        "Scale from single invoices to thousands per day",
        "API access available for integration"
      ],
      howItWorks: [
        { step: 1, title: "Upload PDF Files", description: "Select one or multiple PDF invoices for batch conversion" },
        { step: 2, title: "Smart Data Mapping", description: "AI maps your invoice data to CII schema with intelligent field recognition" },
        { step: 3, title: "Export XRechnung CII", description: "Download validated CII XML files ready for your workflow" }
      ],
      faq: [
        { 
          question: "What's the difference between UBL and CII format?", 
          answer: "CII (Cross Industry Invoice) is based on UN/CEFACT standards and is often preferred for ZUGFeRD implementations. UBL is more common in Peppol networks. Both are valid XRechnung formats." 
        },
        { 
          question: "Can I convert ZUGFeRD PDFs to pure CII?", 
          answer: "Yes, our converter can extract the XML component from ZUGFeRD PDFs and convert it to standalone CII format, or create new CII files from regular PDFs." 
        },
        { 
          question: "Do you support batch processing?", 
          answer: "Yes, you can upload up to 10 invoices at once for batch conversion. For larger volumes, contact us about our API solution with unlimited processing." 
        }
      ]
    },

    // PDF to ZUGFeRD
    pdfToZugferd: {
      title: "PDF to ZUGFeRD Converter - Hybrid Invoice Solution",
      description: "Create ZUGFeRD hybrid invoices with PDF and embedded XML. Perfect for German B2B requiring visual and machine-readable formats.",
      features: [
        "Generate ZUGFeRD 2.2 compliant hybrid PDFs",
        "Embed XRechnung XML directly into PDF/A-3",
        "Support for all ZUGFeRD profiles (Basic, Comfort, Extended)",
        "Automatic PDF/A-3 conversion and validation",
        "Preserve original PDF layout and design",
        "Digital signature support for authenticity",
        "Compatible with all German tax requirements"
      ],
      benefits: [
        "One file serves both human and machine processing",
        "Future-proof your invoicing with dual format",
        "Simplified archiving with embedded structured data",
        "Seamless integration with German accounting software",
        "Reduce errors in invoice processing by 95%",
        "Comply with GoBD archiving requirements"
      ],
      howItWorks: [
        { step: 1, title: "Upload Original PDF", description: "Provide your existing PDF invoice for enhancement" },
        { step: 2, title: "Extract & Validate Data", description: "AI extracts invoice data and validates against ZUGFeRD schema" },
        { step: 3, title: "Download Hybrid PDF", description: "Receive PDF/A-3 with embedded XML meeting all requirements" }
      ],
      faq: [
        { 
          question: "What is a ZUGFeRD invoice?", 
          answer: "ZUGFeRD is a hybrid invoice format that combines a human-readable PDF with embedded structured XML data. It allows both manual review and automated processing of the same invoice file." 
        },
        { 
          question: "Which ZUGFeRD version do you support?", 
          answer: "We support ZUGFeRD 2.2, which is fully compatible with XRechnung and European standard EN 16931. This ensures maximum compatibility across German and European systems." 
        },
        { 
          question: "Can recipients without special software read ZUGFeRD?", 
          answer: "Yes! ZUGFeRD invoices appear as normal PDFs in any PDF reader. The embedded XML data is only accessed by specialized accounting software when needed." 
        }
      ]
    },

    // XRechnung to PDF
    xrechnungToPdf: {
      title: "XRechnung to PDF Converter - Visualization Tool",
      description: "Convert XRechnung XML (UBL/CII) to professional PDF invoices. Perfect for archiving, review, or sharing visual formats.",
      features: [
        "Support for both UBL and CII XRechnung formats",
        "Professional invoice templates with customization",
        "Automatic layout optimization for readability",
        "Multi-language PDF generation (DE, EN, FR, IT, ES)",
        "Barcode and QR code generation for payment data",
        "Company logo and branding integration",
        "Batch conversion for multiple XML files"
      ],
      benefits: [
        "Make XML invoices readable for non-technical users",
        "Create print-ready documents for physical archiving",
        "Maintain visual consistency across your invoices",
        "Enable easy sharing via email or print",
        "Preserve all XRechnung data in visual format",
        "Generate customer-friendly invoice copies"
      ],
      howItWorks: [
        { step: 1, title: "Upload XML File", description: "Select your XRechnung XML file (UBL or CII format)" },
        { step: 2, title: "Choose Template", description: "Select from professional templates or use automatic layout" },
        { step: 3, title: "Download PDF", description: "Get your formatted PDF invoice ready for use" }
      ],
      faq: [
        { 
          question: "Will all XML data appear in the PDF?", 
          answer: "Yes, our converter ensures all relevant invoice data from the XML is displayed in the PDF, including line items, taxes, payment terms, and party information." 
        },
        { 
          question: "Can I customize the PDF layout?", 
          answer: "Currently, we offer optimized automatic layouts. Custom branding and templates are available for subscribed users and API customers." 
        },
        { 
          question: "Is the generated PDF legally valid?", 
          answer: "The PDF serves as a visual representation of the XRechnung data. For legal compliance in e-invoicing, the original XML file should be retained as the authoritative version." 
        }
      ]
    },

    // XRechnung Viewer
    xrechnungViewer: {
      title: "XRechnung Viewer - Validation & Display Tool",
      description: "View, validate & analyze XRechnung files online. Comprehensive validation with official rules and easy-to-read display.",
      features: [
        "Real-time XRechnung validation (UBL & CII)",
        "Detailed error reporting with BR-DE rule checking",
        "Syntax highlighting for XML structure",
        "Side-by-side view of raw XML and formatted data",
        "Export validation reports for compliance",
        "Support for all XRechnung versions (1.2 to 2.3)",
        "Calculate and verify invoice totals automatically"
      ],
      benefits: [
        "Ensure compliance before submission",
        "Identify and fix errors immediately",
        "No installation required - works in any browser",
        "Understand XRechnung structure easily",
        "Verify supplier invoices before processing",
        "Learn XRechnung format with visual guidance"
      ],
      howItWorks: [
        { step: 1, title: "Upload XRechnung", description: "Drop your XML file or paste XML content directly" },
        { step: 2, title: "Automatic Validation", description: "Instant validation against official XRechnung rules" },
        { step: 3, title: "Review Results", description: "See formatted invoice data and any validation issues" }
      ],
      faq: [
        { 
          question: "Which validation rules are checked?", 
          answer: "We validate against all official XRechnung schematron rules, including BR-DE business rules, ensuring your invoice meets German public sector requirements." 
        },
        { 
          question: "Can I edit invoices in the viewer?", 
          answer: "The viewer is read-only for security. To edit XRechnung files, first convert to our editable format, make changes, then export back to XML." 
        },
        { 
          question: "Is sensitive data secure in the viewer?", 
          answer: "Yes, all validation happens in your browser. For uploaded files, data is processed on EU servers and automatically deleted after viewing." 
        }
      ]
    }
  },

  de: {
    // PDF zu XRechnung (UBL)
    pdfToUbl: {
      title: "PDF zu XRechnung (UBL) Konverter - Komplettlösung",
      description: "PDF zu XRechnung UBL konvertieren. KI-Konverter für deutsche Behörden-Compliance (EN 16931) und Peppol-Integration.",
      features: [
        "KI-gestützte Datenextraktion aus PDF-Rechnungen mit 99% Genauigkeit",
        "Volle Konformität mit XRechnung 2.3 und EN 16931 Standards",
        "Automatische Validierung von Pflichtfeldern (BR-DE-Regeln)",
        "Unterstützung aller Rechnungstypen (380, 381, 384, 389)",
        "Stapelverarbeitung für mehrere Rechnungen",
        "Sofortige Vorschau und Bearbeitung vor dem Download",
        "Peppol-fähiges UBL 2.1 Format"
      ],
      benefits: [
        "Sparen Sie 90% Zeit im Vergleich zur manuellen Dateneingabe",
        "Eliminieren Sie Fehler durch automatische Validierung",
        "Erfüllen Sie deutsche Behördenanforderungen sofort",
        "Keine Softwareinstallation erforderlich - 100% webbasiert",
        "Sichere Verarbeitung - Ihre Daten verlassen nie EU-Server",
        "24/7 Verfügbarkeit mit Sofort-Konvertierung"
      ],
      howItWorks: [
        { step: 1, title: "PDF-Rechnung hochladen", description: "Ziehen Sie Ihre PDF-Datei per Drag & Drop oder wählen Sie sie aus (bis zu 10 Dateien, je 5MB)" },
        { step: 2, title: "KI-Extraktion & Prüfung", description: "Unsere KI extrahiert alle Rechnungsdaten automatisch. Prüfen und bearbeiten Sie bei Bedarf jedes Feld" },
        { step: 3, title: "XRechnung UBL herunterladen", description: "Erhalten Sie Ihre validierte XRechnung UBL XML-Datei zur Einreichung" }
      ],
      faq: [
        { 
          question: "Was ist das XRechnung UBL-Format?", 
          answer: "XRechnung UBL (Universal Business Language) ist das standardisierte XML-Format für E-Rechnungen an deutsche Behörden. Es basiert auf UBL 2.1 und gewährleistet die Konformität mit dem europäischen Standard EN 16931." 
        },
        { 
          question: "Welche Organisationen verlangen XRechnung UBL?", 
          answer: "Alle deutschen Bundesbehörden, die meisten Landesbehörden und viele Kommunen verlangen das XRechnung-Format. Die UBL-Variante ist besonders für Peppol-Netzwerk-Übertragungen üblich." 
        },
        { 
          question: "Wie genau ist die PDF-Extraktion?", 
          answer: "Unsere KI erreicht 99% Genauigkeit bei Standard-Rechnungslayouts. Alle extrahierten Daten können vor der Konvertierung überprüft und bearbeitet werden, was 100% Genauigkeit im Endergebnis sicherstellt." 
        },
        {
          question: "Sind meine Rechnungsdaten sicher?",
          answer: "Ja, die gesamte Verarbeitung erfolgt auf EU-basierten Servern mit Verschlüsselung auf Bankniveau. Ihre Daten werden nach 24 Stunden automatisch gelöscht, und wir speichern oder teilen Ihre Rechnungsinformationen niemals."
        }
      ]
    },

    // PDF zu XRechnung (CII)
    pdfToCii: {
      title: "PDF zu XRechnung (CII) Konverter - Unternehmenslösung",
      description: "PDF zu XRechnung CII für B2B/B2G-Transaktionen. Unterstützt Cross Industry Invoice Standard und ZUGFeRD.",
      features: [
        "Fortschrittliche OCR und KI für komplexe Rechnungslayouts",
        "CII 16B-Format mit voller UN/CEFACT-Konformität",
        "Integrierte ZUGFeRD 2.2 Kompatibilität",
        "Automatische Steuerberechnung und -validierung",
        "Mehrsprachige Rechnungsunterstützung (DE, EN, FR, IT, ES)",
        "Datenextraktion auf Positionsebene",
        "Export in Buchhaltungssysteme (DATEV, SAP)"
      ],
      benefits: [
        "Perfekt für Unternehmens-B2B-Rechnungsworkflows",
        "Kompatibel mit allen großen ERP-Systemen",
        "Reduzieren Sie Verarbeitungskosten um bis zu 80%",
        "Stellen Sie Steuer-Compliance durch automatische Validierung sicher",
        "Skalieren Sie von Einzelrechnungen auf Tausende pro Tag",
        "API-Zugang für Integration verfügbar"
      ],
      howItWorks: [
        { step: 1, title: "PDF-Dateien hochladen", description: "Wählen Sie eine oder mehrere PDF-Rechnungen für die Stapelkonvertierung" },
        { step: 2, title: "Intelligente Datenzuordnung", description: "KI ordnet Ihre Rechnungsdaten dem CII-Schema mit intelligenter Felderkennung zu" },
        { step: 3, title: "XRechnung CII exportieren", description: "Laden Sie validierte CII XML-Dateien für Ihren Workflow herunter" }
      ],
      faq: [
        { 
          question: "Was ist der Unterschied zwischen UBL und CII-Format?", 
          answer: "CII (Cross Industry Invoice) basiert auf UN/CEFACT-Standards und wird oft für ZUGFeRD-Implementierungen bevorzugt. UBL ist in Peppol-Netzwerken verbreiteter. Beide sind gültige XRechnung-Formate." 
        },
        { 
          question: "Kann ich ZUGFeRD-PDFs in reines CII konvertieren?", 
          answer: "Ja, unser Konverter kann die XML-Komponente aus ZUGFeRD-PDFs extrahieren und in eigenständiges CII-Format konvertieren oder neue CII-Dateien aus regulären PDFs erstellen." 
        },
        { 
          question: "Unterstützen Sie Stapelverarbeitung?", 
          answer: "Ja, Sie können bis zu 10 Rechnungen gleichzeitig für die Stapelkonvertierung hochladen. Für größere Mengen kontaktieren Sie uns bezüglich unserer API-Lösung mit unbegrenzter Verarbeitung." 
        }
      ]
    },

    // PDF zu ZUGFeRD
    pdfToZugferd: {
      title: "PDF zu ZUGFeRD Konverter - Hybride Rechnungslösung",
      description: "ZUGFeRD-Hybridrechnungen mit PDF und eingebettetem XML erstellen. Perfekt für deutsche B2B mit beiden Formaten.",
      features: [
        "Generierung ZUGFeRD 2.2 konformer Hybrid-PDFs",
        "Einbettung von XRechnung XML direkt in PDF/A-3",
        "Unterstützung aller ZUGFeRD-Profile (Basic, Comfort, Extended)",
        "Automatische PDF/A-3 Konvertierung und Validierung",
        "Beibehaltung des Original-PDF-Layouts",
        "Unterstützung digitaler Signaturen für Authentizität",
        "Kompatibel mit allen deutschen Steueranforderungen"
      ],
      benefits: [
        "Eine Datei für menschliche und maschinelle Verarbeitung",
        "Zukunftssichere Rechnungsstellung mit Dualformat",
        "Vereinfachte Archivierung mit eingebetteten strukturierten Daten",
        "Nahtlose Integration mit deutscher Buchhaltungssoftware",
        "Reduzierung von Fehlern bei der Rechnungsverarbeitung um 95%",
        "Erfüllung der GoBD-Archivierungsanforderungen"
      ],
      howItWorks: [
        { step: 1, title: "Original-PDF hochladen", description: "Laden Sie Ihre bestehende PDF-Rechnung zur Erweiterung hoch" },
        { step: 2, title: "Daten extrahieren & validieren", description: "KI extrahiert Rechnungsdaten und validiert gegen ZUGFeRD-Schema" },
        { step: 3, title: "Hybrid-PDF herunterladen", description: "Erhalten Sie PDF/A-3 mit eingebettetem XML, das alle Anforderungen erfüllt" }
      ],
      faq: [
        { 
          question: "Was ist eine ZUGFeRD-Rechnung?", 
          answer: "ZUGFeRD ist ein hybrides Rechnungsformat, das ein menschenlesbares PDF mit eingebetteten strukturierten XML-Daten kombiniert. Es ermöglicht sowohl manuelle Prüfung als auch automatisierte Verarbeitung derselben Rechnungsdatei." 
        },
        { 
          question: "Welche ZUGFeRD-Version unterstützen Sie?", 
          answer: "Wir unterstützen ZUGFeRD 2.2, das vollständig mit XRechnung und dem europäischen Standard EN 16931 kompatibel ist. Dies gewährleistet maximale Kompatibilität in deutschen und europäischen Systemen." 
        },
        { 
          question: "Können Empfänger ohne Spezialsoftware ZUGFeRD lesen?", 
          answer: "Ja! ZUGFeRD-Rechnungen erscheinen als normale PDFs in jedem PDF-Reader. Die eingebetteten XML-Daten werden nur von spezialisierter Buchhaltungssoftware bei Bedarf abgerufen." 
        }
      ]
    },

    // XRechnung zu PDF
    xrechnungToPdf: {
      title: "XRechnung zu PDF Konverter - Visualisierungstool",
      description: "XRechnung XML (UBL/CII) zu professionellen PDFs konvertieren. Ideal für Archivierung, Prüfung oder visuelle Formate.",
      features: [
        "Unterstützung für UBL und CII XRechnung-Formate",
        "Professionelle Rechnungsvorlagen mit Anpassung",
        "Automatische Layout-Optimierung für Lesbarkeit",
        "Mehrsprachige PDF-Generierung (DE, EN, FR, IT, ES)",
        "Barcode- und QR-Code-Generierung für Zahlungsdaten",
        "Firmenlogo und Branding-Integration",
        "Stapelkonvertierung für mehrere XML-Dateien"
      ],
      benefits: [
        "Machen Sie XML-Rechnungen für nicht-technische Nutzer lesbar",
        "Erstellen Sie druckfertige Dokumente für physische Archivierung",
        "Bewahren Sie visuelle Konsistenz über Ihre Rechnungen",
        "Ermöglichen Sie einfaches Teilen per E-Mail oder Druck",
        "Erhalten Sie alle XRechnung-Daten im visuellen Format",
        "Generieren Sie kundenfreundliche Rechnungskopien"
      ],
      howItWorks: [
        { step: 1, title: "XML-Datei hochladen", description: "Wählen Sie Ihre XRechnung XML-Datei (UBL oder CII-Format)" },
        { step: 2, title: "Vorlage wählen", description: "Wählen Sie aus professionellen Vorlagen oder nutzen Sie automatisches Layout" },
        { step: 3, title: "PDF herunterladen", description: "Erhalten Sie Ihre formatierte PDF-Rechnung einsatzbereit" }
      ],
      faq: [
        { 
          question: "Erscheinen alle XML-Daten im PDF?", 
          answer: "Ja, unser Konverter stellt sicher, dass alle relevanten Rechnungsdaten aus dem XML im PDF angezeigt werden, einschließlich Positionen, Steuern, Zahlungsbedingungen und Parteieninformationen." 
        },
        { 
          question: "Kann ich das PDF-Layout anpassen?", 
          answer: "Derzeit bieten wir optimierte automatische Layouts. Individuelles Branding und Vorlagen sind für abonnierte Nutzer und API-Kunden verfügbar." 
        },
        { 
          question: "Ist das generierte PDF rechtsgültig?", 
          answer: "Das PDF dient als visuelle Darstellung der XRechnung-Daten. Für die rechtliche Compliance im E-Invoicing sollte die Original-XML-Datei als maßgebliche Version aufbewahrt werden." 
        }
      ]
    },

    // XRechnung Viewer
    xrechnungViewer: {
      title: "XRechnung Viewer - Validierungs- & Anzeigetool",
      description: "XRechnung online anzeigen, validieren & analysieren. Umfassende Validierung mit offiziellen Regeln und lesbarer Anzeige.",
      features: [
        "Echtzeit-XRechnung-Validierung (UBL & CII)",
        "Detaillierte Fehlerberichterstattung mit BR-DE-Regelprüfung",
        "Syntax-Highlighting für XML-Struktur",
        "Nebeneinander-Ansicht von Roh-XML und formatierten Daten",
        "Export von Validierungsberichten für Compliance",
        "Unterstützung aller XRechnung-Versionen (1.2 bis 2.3)",
        "Automatische Berechnung und Überprüfung von Rechnungssummen"
      ],
      benefits: [
        "Stellen Sie Compliance vor Einreichung sicher",
        "Identifizieren und beheben Sie Fehler sofort",
        "Keine Installation erforderlich - funktioniert in jedem Browser",
        "Verstehen Sie die XRechnung-Struktur einfach",
        "Überprüfen Sie Lieferantenrechnungen vor der Verarbeitung",
        "Lernen Sie das XRechnung-Format mit visueller Anleitung"
      ],
      howItWorks: [
        { step: 1, title: "XRechnung hochladen", description: "Ziehen Sie Ihre XML-Datei hinein oder fügen Sie XML-Inhalt direkt ein" },
        { step: 2, title: "Automatische Validierung", description: "Sofortige Validierung gegen offizielle XRechnung-Regeln" },
        { step: 3, title: "Ergebnisse prüfen", description: "Sehen Sie formatierte Rechnungsdaten und eventuelle Validierungsprobleme" }
      ],
      faq: [
        { 
          question: "Welche Validierungsregeln werden geprüft?", 
          answer: "Wir validieren gegen alle offiziellen XRechnung-Schematron-Regeln, einschließlich BR-DE-Geschäftsregeln, um sicherzustellen, dass Ihre Rechnung den Anforderungen des deutschen öffentlichen Sektors entspricht." 
        },
        { 
          question: "Kann ich Rechnungen im Viewer bearbeiten?", 
          answer: "Der Viewer ist aus Sicherheitsgründen schreibgeschützt. Um XRechnung-Dateien zu bearbeiten, konvertieren Sie sie zuerst in unser bearbeitbares Format, nehmen Sie Änderungen vor und exportieren Sie dann zurück zu XML." 
        },
        { 
          question: "Sind sensible Daten im Viewer sicher?", 
          answer: "Ja, alle Validierungen erfolgen in Ihrem Browser. Bei hochgeladenen Dateien werden Daten auf EU-Servern verarbeitet und nach der Anzeige automatisch gelöscht." 
        }
      ]
    }
  }
};